import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ChevronDown, Ticket } from "lucide-react";
import { Button } from "./ui/button";

const eventsDropdown = [
  { path: "/ramanavami-2026", label: "Ramanavami", date: "Mar 27, 2026" },
  // Vishu hidden per user request - { path: "/vishu-2026", label: "Vishu Maholsavam 2026", date: "Apr 19, 2026" },
  { path: "/picnic-2026", label: "Annual Picnic 2026", date: "Jul 11, 2026" },
  { path: "/karkidaka-vavu-bali-2026", label: "Karkidaka Vavu Bali", date: "Aug 12, 2026" },
  { path: "/diwali-2026", label: "Diwali Kudumbasangamam", date: "Nov 7, 2026" },
  { path: "/vidhyarambam-2026", label: "Vidhyarambam", date: "Oct 21, 2026" },
  { path: "/mandalakalam-2026", label: "Mandalakalam", date: "Nov 17, 2026" },
];

const sponsorshipDropdown = [
  { path: "/sponsorship/onam", label: "Onam 2026" },
  { path: "/sponsorship/picnic", label: "Annual Picnic 2026" },
  { path: "/sponsors", label: "All Sponsorship Options" },
];

const charityDropdown = [
  { path: "/charity/food-drive", label: "Food Drive" },
  { path: "/charity/blood-donation", label: "Blood Donation" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [memberLoggedIn, setMemberLoggedIn] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    const token = localStorage.getItem("member_token");
    setMemberLoggedIn(!!token);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMemberLogout = () => {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_data");
    setMemberLoggedIn(false);
    navigate("/");
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const isHomePage = location.pathname === "/";
  const textColor = scrolled || !isHomePage ? "text-kerala-green" : "text-white";
  const textColorMuted = scrolled || !isHomePage ? "text-kerala-green/80" : "text-white/90";
  const hoverColor = scrolled || !isHomePage ? "hover:text-kerala-green" : "hover:text-white";

  const DropdownMenu = ({ name, items, showDates = false }) => (
    <div className="relative">
      <button
        onClick={() => toggleDropdown(name)}
        className={`flex items-center gap-1 font-medium transition-colors cursor-pointer ${textColorMuted} ${hoverColor}`}
      >
        {name}
        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === name ? "rotate-180" : ""}`} />
      </button>
      
      {activeDropdown === name && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-kerala-gold/20 py-2 z-50">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center justify-between px-4 py-3 hover:bg-kerala-cream transition-colors cursor-pointer"
            >
              <span className="font-medium text-kerala-green">{item.label}</span>
              {showDates && item.date && (
                <span className="text-xs text-kerala-green/60">{item.date}</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHomePage
          ? "bg-white/95 backdrop-blur-md shadow-sm" 
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-20" ref={dropdownRef}>
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <img 
              src="/logo.jpg"
              alt="Tatvamasi Logo"
              className="w-16 h-16 object-contain"
            />
            <div className="hidden sm:block">
              <h1 className={`font-heading font-semibold text-xl leading-tight ${textColor}`}>
                Tatvamasi
              </h1>
              <p className={`text-xs ${scrolled || !isHomePage ? "text-kerala-green/60" : "text-white/70"}`}>
                Kerala Hindu's of London Ontario
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Home */}
            <Link
              to="/"
              className={`font-medium transition-colors ${
                location.pathname === "/" 
                  ? (scrolled || !isHomePage ? "text-kerala-terracotta" : "text-kerala-gold")
                  : `${textColorMuted} ${hoverColor}`
              }`}
            >
              Home
            </Link>

            {/* Events Dropdown */}
            <DropdownMenu name="Events" items={eventsDropdown} showDates={true} />

            {/* Gallery */}
            <Link
              to="/gallery"
              className={`font-medium transition-colors ${
                location.pathname === "/gallery" 
                  ? (scrolled || !isHomePage ? "text-kerala-terracotta" : "text-kerala-gold")
                  : `${textColorMuted} ${hoverColor}`
              }`}
            >
              Gallery
            </Link>

            {/* Sponsorship Dropdown */}
            <DropdownMenu name="Sponsorship" items={sponsorshipDropdown} />

            {/* Contact */}
            <Link
              to="/contact"
              className={`font-medium transition-colors ${
                location.pathname === "/contact" 
                  ? (scrolled || !isHomePage ? "text-kerala-terracotta" : "text-kerala-gold")
                  : `${textColorMuted} ${hoverColor}`
              }`}
            >
              Contact
            </Link>

            {/* Charity Dropdown */}
            <DropdownMenu name="Charity" items={charityDropdown} />
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            {memberLoggedIn ? (
              <>
                <Link to="/my-tickets">
                  <Button 
                    variant="ghost"
                    size="sm"
                    className={`${scrolled || !isHomePage ? "text-kerala-green hover:text-kerala-terracotta" : "text-white hover:text-kerala-gold"}`}
                  >
                    <Ticket className="w-4 h-4 mr-1" />
                    My Tickets
                  </Button>
                </Link>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={handleMemberLogout}
                  className={`${scrolled || !isHomePage ? "text-kerala-green hover:text-kerala-terracotta" : "text-white hover:text-kerala-gold"}`}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost"
                    size="sm"
                    className={`${scrolled || !isHomePage ? "text-kerala-green hover:text-kerala-terracotta" : "text-white hover:text-kerala-gold"} cursor-pointer`}
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="ghost"
                    size="sm"
                    className={`${scrolled || !isHomePage ? "text-kerala-green hover:text-kerala-terracotta" : "text-white hover:text-kerala-gold"} cursor-pointer`}
                  >
                    Register
                  </Button>
                </Link>
              </>
            )}
            <Link to="/diwali-2026">
              <Button className="btn-primary cursor-pointer" size="sm">
                Book Tickets
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 ${textColor}`}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-kerala-gold/10">
          <div className="section-container py-4 space-y-2">
            <Link
              to="/"
              className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                location.pathname === "/" ? "bg-kerala-green/10 text-kerala-terracotta" : "text-kerala-green hover:bg-kerala-green/5"
              }`}
            >
              Home
            </Link>
            
            {/* Mobile Events */}
            <div className="py-2 px-4">
              <p className="text-sm font-semibold text-kerala-green/60 mb-2">Events</p>
              <div className="space-y-1 pl-2">
                {eventsDropdown.map((event) => (
                  <Link
                    key={event.path}
                    to={event.path}
                    className="flex items-center justify-between py-2 px-3 rounded-lg text-kerala-green hover:bg-kerala-green/5 cursor-pointer"
                  >
                    <span>{event.label}</span>
                    <span className="text-xs text-kerala-green/60">{event.date}</span>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/gallery"
              className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                location.pathname === "/gallery" ? "bg-kerala-green/10 text-kerala-terracotta" : "text-kerala-green hover:bg-kerala-green/5"
              }`}
            >
              Gallery
            </Link>

            {/* Mobile Sponsorship */}
            <div className="py-2 px-4">
              <p className="text-sm font-semibold text-kerala-green/60 mb-2">Sponsorship</p>
              <div className="space-y-1 pl-2">
                {sponsorshipDropdown.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 px-3 rounded-lg text-kerala-green hover:bg-kerala-green/5 cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/contact"
              className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                location.pathname === "/contact" ? "bg-kerala-green/10 text-kerala-terracotta" : "text-kerala-green hover:bg-kerala-green/5"
              }`}
            >
              Contact
            </Link>

            {/* Mobile Charity */}
            <div className="py-2 px-4">
              <p className="text-sm font-semibold text-kerala-green/60 mb-2">Charity</p>
              <div className="space-y-1 pl-2">
                {charityDropdown.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 px-3 rounded-lg text-kerala-green hover:bg-kerala-green/5 cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="pt-4 space-y-2">
              {memberLoggedIn ? (
                <>
                  <Link to="/my-tickets" className="block">
                    <Button variant="outline" className="w-full cursor-pointer">
                      <Ticket className="w-4 h-4 mr-2" />
                      My Tickets
                    </Button>
                  </Link>
                  <Button variant="ghost" className="w-full cursor-pointer" onClick={handleMemberLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full cursor-pointer">Login</Button>
                  </Link>
                  <Link to="/register" className="block">
                    <Button variant="outline" className="w-full cursor-pointer">Register</Button>
                  </Link>
                </>
              )}
              <Link to="/diwali-2026" className="block">
                <Button className="btn-primary w-full cursor-pointer">Book Tickets</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
